/**
 * Locale formatting without a lossy Number conversion. Нули в конце дробной части
 * не показываем: «95,00000000 м³» → «95 м³», «0,06000000» → «0,06». Точность не
 * теряется — сервер хранит и выгружает значение целиком.
 */
export function decimal(value: string | null | undefined, suffix = ''): string {
  if (value == null) return '—';
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return value;
  const fraction = (match[3] ?? '').replace(/0+$/, '');
  return `${match[1]}${match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${fraction ? `,${fraction}` : ''}${suffix}`;
}

/** Часы и минуты момента в часовом поясе проекта. */
function localTime(date: Date, timezone: string): { hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { hour: pick('hour'), minute: pick('minute') };
}

/**
 * Дата и время в поясе проекта. Начало суток (00:00) и конец суток (23:59) — граница
 * дня, а не время события: такие моменты показываем одной датой.
 */
export function projectDate(value: string | null | undefined, timezone: string): string {
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.split('-').reverse().join('.');
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const { hour, minute } = localTime(date, timezone);
  const boundary = (hour === 0 && minute === 0) || (hour === 23 && minute === 59);
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: timezone,
    dateStyle: 'short',
    ...(boundary ? {} : { timeStyle: 'short' }),
  }).format(date);
}

export function projectMonth(value: string, timezone: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(0, 7);
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(instant);
  return `${parts.find((part) => part.type === 'year')?.value}-${parts.find((part) => part.type === 'month')?.value}`;
}

const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'мая',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
];

/** Год, месяц и день момента в поясе проекта (дата «ГГГГ-ММ-ДД» — как есть). */
export function projectDay(
  value: string,
  timezone: string,
): { y: number; m: number; d: number } | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value))
    return {
      y: Number(value.slice(0, 4)),
      m: Number(value.slice(5, 7)),
      d: Number(value.slice(8, 10)),
    };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const pick = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { y: pick('year'), m: pick('month'), d: pick('day') };
}

/** Короткая дата «12 окт»; год — только если не текущий: «21 авг 2027». */
export function shortDate(value: string | null | undefined, timezone: string): string {
  if (!value) return '—';
  const day = projectDay(value, timezone);
  if (!day) return value;
  const thisYear = new Date().getFullYear();
  return `${day.d} ${MONTHS_SHORT[day.m - 1]}${day.y === thisYear ? '' : ` ${day.y}`}`;
}

/** Подпись месяца на шкале: «ноя 26». */
export function monthTick(value: string, timezone: string): string {
  const day = projectDay(value, timezone);
  if (!day) return value;
  return `${MONTHS_SHORT[day.m - 1]} ${String(day.y).slice(2)}`;
}

/**
 * Общий резерв работы. Сервер считает его в календарных минутах (разница моментов),
 * человеку нужны дни: «−99361» → «−69 дн.». Меньше суток — в часах.
 */
export function floatDays(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  const sign = minutes < 0 ? '−' : '';
  const abs = Math.abs(minutes);
  if (abs < 1440) {
    const hours = Math.round(abs / 60);
    return hours === 0 ? '0 дн.' : `${sign}${hours} ч`;
  }
  return `${sign}${Math.round(abs / 1440).toLocaleString('ru-RU')} дн.`;
}

/** Смещение пояса проекта для подсказки: «UTC+8». */
/** Тип связи работ словами (FS — окончание предшественника → начало последователя). */
export const LINK_KIND: Record<string, string> = {
  FS: 'Окончание → начало',
  SS: 'Начало → начало',
  FF: 'Окончание → окончание',
  SF: 'Начало → окончание',
};

/** Лаг связи: целые дни — «2 дн.», целые часы — «3 ч», иначе минуты. */
export const lagText = (minutes: number) =>
  minutes % 1440 === 0
    ? `${minutes / 1440} дн.`
    : minutes % 60 === 0
      ? `${minutes / 60} ч`
      : `${minutes} мин`;

/** Связь словами: «Окончание → начало · лаг +2 дн. рабочих». */
export function linkLabel(edge: { kind: string; lag_minutes: number; lag_kind?: string }): string {
  const lag = edge.lag_minutes
    ? ` · лаг ${edge.lag_minutes > 0 ? '+' : '−'}${lagText(Math.abs(edge.lag_minutes))}${edge.lag_kind === 'working' ? ' рабочих' : ''}`
    : '';
  return `${LINK_KIND[edge.kind] ?? edge.kind}${lag}`;
}

export function timezoneOffset(timezone: string): string {
  try {
    const name = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value;
    return name ? name.replace('GMT', 'UTC') : timezone;
  } catch {
    return timezone;
  }
}

/** Календарные дни между двумя моментами (b − a), с округлением. */
export function daysBetween(
  a: string | null | undefined,
  b: string | null | undefined,
): number | null {
  if (!a || !b) return null;
  const from = Date.parse(a);
  const to = Date.parse(b);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  return Math.round((to - from) / 86400000);
}

/** «N дней» с правильным окончанием. */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

export const statusLabel: Record<string, string> = {
  draft: 'Проект',
  uploaded: 'Загружен',
  parsing: 'Разбор',
  needs_review: 'Требует проверки',
  approved_baseline_missing: 'Нет утверждённого базового графика',
  approved_weights_missing: 'Не утверждены веса работ для расчёта общего прогресса',
  weights_not_normalized: 'Веса работ не приведены к общей сумме',
  reporting_gap: 'Не хватает подтверждённых данных о выполнении',
  assumed_supply: 'Наличие материалов принято как допущение',
  monthly_plan_missing: 'Нет выпущенного месячного задания',
  period_not_released: 'Месячное задание ещё не выпущено',
  constraint_violation: 'Прогноз нарушает договорные или директивные сроки работ',
  negative_float: 'Отрицательный резерв: прогноз позже договорных сроков',
  expected_rd_catalogue_unknown: 'Не задан перечень ожидаемой РД — полнота РД не оценена',
  partial_rd_catalogue: 'Перечень РД неполный',
  rd_catalogue_needs_review: 'Перечень РД требует проверки',
  unmapped_accepted_fact: 'Есть принятый факт без привязки к работам графика',
  started_remaining_unknown: 'Не задан остаток длительности начатых работ',
  rejected: 'Отклонено',
  accepted: 'Принято',
  returned: 'Возвращено',
  pending: 'Ожидает приёмки',
  approved: 'Утверждён',
  approved_customer: 'Согласован Заказчиком',
  approved_internal: 'Утверждён внутри организации',
  internal_review: 'На внутреннем согласовании',
  detailed_baseline: 'Детальный базовый',
  complete_rd: 'Полная подтверждённая РД',
  confirmed: 'Подтверждено',
  rd: 'Рабочая документация',
  supply: 'Поставки',
  actual: 'Выполнение',
  id: 'Устойчивый ID',
  name: 'Наименование',
  quantity: 'Количество',
  unit: 'Единица',
  duration_minutes: 'Длительность, минуты',
  calendar_id: 'Календарь',
  wbs_id: 'Раздел WBS',
  activity_id: 'Работа',
  performed_date: 'Дата выполнения',
  labour_weight: 'Подтверждённый вес',
  record_uid: 'Код позиции источника',
  source_ref: 'Источник',
  submitted: 'На согласовании',
  duplicate_review: 'Изменение источника — требуется проверка',
  released: 'Выпущен',
  signed: 'Подписан',
  dispatched: 'Направлен',
  queued: 'В очереди',
  running: 'Расчёт выполняется',
  processing: 'Обработка выполняется',
  completed: 'Обработка завершена',
  applied: 'Применён в график',
  succeeded: 'Рассчитан',
  failed: 'Ошибка',
  cancelled: 'Отменён',
  open: 'Открыто',
  closed_by_letter: 'Закрыто по письму',
  unknown: 'Неизвестно',
  partial_rd: 'По подтверждённой части РД',
  full_rd: 'Полная РД',
  confirmed_rd_subset: 'По подтверждённой части РД',
  sent: 'Передано провайдеру',
  delivered: 'Доставлено',
  uncertain: 'Отправка не подтверждена',
  retry: 'Повтор доставки',
  dead: 'Доставка остановлена с ошибкой',
  assumed_available: 'Допущение наличия',
  contract: 'Договорной',
  current: 'Текущий',
  forecast: 'Прогноз',
  scenario: 'Сценарий',
  achieved: 'Достигнута',
  not_achieved: 'Не достигнута',
  verified: 'Подтверждено',
  no_plan: 'Нет плана',
  zero_denominator: 'Нулевой знаменатель',
  approved_quantity_unknown: 'Нет утверждённого объёма для сравнения',
  denominator_unit_unknown: 'Единица утверждённого объёма не подтверждена',
  actual_unit_unknown: 'Единица принятого факта не подтверждена',
  unit_mismatch: 'Единицы объёма и принятого факта различаются',
  unit_mismatch_or_unknown: 'Единицы факта не подтверждены или отличаются',
  unschedulable: 'Нет допустимого размещения',
  physical_progress: 'Физическое выполнение',
  month_execution: 'Выполнение месячного задания',
  rd_count_coverage: 'Полнота РД',
  approved_labour_weights: 'по подтверждённым трудозатратам',
  needs_review_weights: 'по весам работ, требующим проверки',
  current_confirmed_expected_rd_count: 'получено комплектов из ожидаемых',
  manual_weights: 'по утверждённым весам',
  whole_project: 'Весь проект',
  project_rd_catalogue: 'Перечень РД проекта',
  full_project: 'Весь проект',
  partial: 'Частичный охват',
  complete: 'Полный охват',
  ok: 'Доступен',
  unavailable: 'Недоступен',
  stale: 'Данные устарели',
  person: 'Персонал',
  equipment: 'Техника',
  team: 'Бригада',
  field: 'Прораб',
  pto: 'ПТО',
  reconcile: 'Сверка источников',
  manual: 'Ручной ввод ПТО',
  deviation_act: 'Акт фиксации отклонений',
  recovery_plan: 'План ликвидации отставания',
  letter: 'Письмо по вехе',
  period_plan: 'Месячно-суточное задание',
  planfact: 'План-факт',
  schedule: 'График',
  rd_readiness_applied: 'Начало работ перенесено по готовности РД',
  rd_readiness_conditional: 'Прогноз условный: даты РД не подтверждены, взята плановая выдача',
  downstream_rd_readiness_conditional: 'Прогноз зависящих работ условный по датам РД',
  rd_readiness_unknown: 'Готовность РД не определена — сроки работ не рассчитаны',
  downstream_rd_readiness_unknown:
    'Сроки зависящих работ не рассчитаны: готовность РД не определена',
};
export const label = (value: string) => statusLabel[value] ?? value;

/** Код показателя без префикса: «ksp.physical_progress» → «Физическое выполнение». */
export const kpiLabel = (code: string) => label(code.replace(/^ksp\./, ''));

/** Действия журнала КСП словами (ksp.audit_events.action). */
export const auditActionLabel: Record<string, string> = {
  'project.create': 'Проект КСП создан',
  'settings.update': 'Настройки проекта изменены',
  'grant.upsert': 'Выданы права в проекте',
  'department_head.upsert': 'Назначен руководитель отдела',
  'recipient.bind': 'Привязан получатель предупреждений',
  'calendar.create': 'Добавлен рабочий календарь',
  'version.create': 'Создана версия графика',
  'version.clone': 'Создана новая ревизия графика',
  'version.submit': 'График передан на согласование',
  'version.approve': 'График утверждён',
  'wbs.upsert': 'Изменён раздел WBS',
  'activity.upsert': 'Изменена работа',
  'dependency.upsert': 'Изменена связь работ',
  'dependency.delete': 'Удалена связь работ',
  'resource.upsert': 'Изменён ресурс',
  'resource.assign': 'Ресурс назначен работе',
  'supply.bind': 'Привязана поставка',
  'file.register': 'Загружен файл-приложение',
  'import.create': 'Загружены исходные данные',
  'import.decisions': 'Проверены строки импорта',
  'import.confirm': 'Импорт подтверждён',
  'import.apply': 'Импорт применён в график',
  'rd.bind': 'Привязан объём РД',
  'rd.binding_replaced': 'Привязка РД заменена на новую ревизию',
  'rd.link': 'Связан документ РД',
  'rd.confirm': 'Подтверждена дата РД',
  'rd.confirm.revoke': 'Подтверждение даты РД отозвано',
  'rd.requirement.prefill': 'Предложены требования РД к работам',
  'rd.requirement.set': 'Задано требование РД к работе',
  'rd.requirement.confirm': 'Подтверждено требование РД',
  'rd.requirement.remove': 'Требование РД снято',
  'rd.plan.set': 'Задана плановая дата выдачи РД',
  'rd.plan.remove': 'Плановая дата выдачи РД снята',
  'rd.plan.accept': 'Принят план выдачи РД',
  'rd_import.create': 'Загружен план выдачи РД',
  'calculation.queue': 'Запущен расчёт графика',
  'calculation.queued': 'Расчёт графика поставлен в очередь',
  'calculation.publish': 'Расчёт опубликован',
  'calculation.auto_publish': 'Расчёт опубликован автоматически',
  'period.create': 'Создано месячное задание',
  'period.release': 'Месячное задание выпущено',
  'actual.submit': 'Подан факт выполнения',
  'actual.accept': 'Факт принят',
  'actual.return': 'Факт возвращён',
  'actual.correct': 'Компенсирующая корректировка факта',
  'actual.split': 'Факт разделён по источникам',
  'milestone.create': 'Добавлена договорная веха',
  'milestone.revise': 'Новая ревизия срока вехи',
  'milestone.achieve': 'Достижение вехи подтверждено',
  'alert.close': 'Предупреждение по вехе закрыто',
  'alert.close_future': 'Будущее предупреждение закрыто письмом',
  'letter.create': 'Создано письмо по вехе',
  'letter.prepare': 'Подготовлено письмо Заказчику',
  'letter.send': 'Письмо направлено',
  'letter.send_claimed': 'Отправка письма начата',
  'evidence.verify': 'Подтверждено направление письма',
  'evidence.revoke': 'Подтверждение направления отозвано',
  'outgoing.resolve': 'Связано исходящее письмо',
  'delivery.resolve': 'Разобрана доставка предупреждения',
  'deviation.create': 'Составлен акт фиксации отклонений',
  'recovery.create': 'Составлен план ликвидации отставания',
  'recovery.action': 'Изменено мероприятие плана',
  'recovery.approve': 'План ликвидации отставания согласован',
  'proposal.create': 'Помощник подготовил предложение',
  'proposal.apply': 'Применено предложение помощника',
  'export.create': 'Создана выгрузка',
  'export.complete': 'Выгрузка готова',
  'source.sync.request': 'Запрошено обновление источника',
};
export const auditLabel = (action: string) => auditActionLabel[action] ?? action;
